// src/components/module/ventas/PagoVenta.tsx
// Rediseño corporativo estilo Notion/Linear
import React, { useState, useContext } from "react";
import {
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Stack,
  Fade,
  alpha,
  Divider,
} from "@mui/material";
import {
  Paid as PaidIcon, Payment as PaymentIcon, AttachMoney,
  AccountBalanceWallet, CreditCard, Description,
  CheckCircle, ArrowForward, CalendarMonth, CreditScore,
} from "@mui/icons-material";
import { useTiposPagoApi } from "../../../hooks/useTiposPagoApi";
import { toast } from "../../../utils/toast";
import { formatMoney } from "../../../utils/format";
import { DarkModeContext } from "../../../context/darkModeContext";

export interface CreditoInfo {
  NumeroCuotas: number;
  FechaPrimeraCuota: string;
  IntervaloDias: number;
}

interface PagoVentaProps {
  idVenta: number; totalAPagar: number; idSucursal: number;
  onPagar: (d: {
    IdVenta: number; IdTipoPago: number; Abono: number; Observacion: string;
    CreditoInfo?: CreditoInfo;
  }) => Promise<void>;
  onPagoCompletado: () => void;
}

const P = {
  light: { bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF", text: "#37352F", textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7", borderH: "#D3D3D0", accent: "#2383E2", accentBg: "#E8F0FE", success: "#0F7B6C", successBg: "#DBEDDB", warning: "#D9730D", warningBg: "#FADEC9", error: "#E03E3E", hover: "rgba(55,53,47,0.04)" },
  dark: { bg: "#191919", bgS: "#202020", bgT: "#2B2B2B", text: "#E8E8E6", textS: "#9B9A97", textT: "#6B6B6B", border: "#333333", borderH: "#444444", accent: "#529CCA", accentBg: "#143A52", success: "#4DAB9A", successBg: "#1A2F2A", warning: "#C77D48", warningBg: "#2E2215", error: "#D44C47", hover: "rgba(255,255,255,0.04)" },
};

const FieldLabel: React.FC<{ label: string; required?: boolean; color: string }> = ({ label, required, color }) => (
  <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color, mb: 0.5, textTransform: "uppercase", letterSpacing: "0.04em" }}>
    {label}{required && <Box component="span" sx={{ color: "#E03E3E", ml: 0.3 }}>*</Box>}
  </Typography>
);

const defaultFechaPrimeraCuota = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return d.toISOString().split("T")[0];
};

const isCredito = (nombre: string) => /cr[eé]dito/i.test(nombre);

export const PagoVenta: React.FC<PagoVentaProps> = ({ idVenta, totalAPagar, idSucursal, onPagar, onPagoCompletado }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { tiposPago, isLoading } = useTiposPagoApi();
  const [tipoPago, setTipoPago] = useState(0);
  const [montoInput, setMontoInput] = useState("");
  const [abono, setAbono] = useState("");
  const [observacion, setObservacion] = useState("");
  const [cambio, setCambio] = useState(0);
  const [errors, setErrors] = useState({ tipoPago: false, abono: false, fechaCuota: false });
  // Crédito
  const [numeroCuotas, setNumeroCuotas] = useState(3);
  const [fechaPrimeraCuota, setFechaPrimeraCuota] = useState(defaultFechaPrimeraCuota());
  const [intervaloDias, setIntervaloDias] = useState(30);
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  const selectedTipo = tiposPago.find(t => t.Id === tipoPago);
  const esCreditoTipo = selectedTipo ? isCredito(selectedTipo.Nombre) : false;
  const valorCuotaCalc = esCreditoTipo && numeroCuotas > 0
    ? parseFloat((totalAPagar / numeroCuotas).toFixed(2))
    : 0;

  const handleTipoPagoChange = (v: number) => {
    setTipoPago(v);
    const tipo = tiposPago.find(t => t.Id === v);
    if (tipo && isCredito(tipo.Nombre)) {
      setAbono(totalAPagar.toString());
      setMontoInput(formatMoney(totalAPagar));
      setCambio(0);
    } else if (v !== 1) {
      setAbono(totalAPagar.toString());
      setMontoInput(formatMoney(totalAPagar));
      setCambio(0);
    } else {
      setAbono(""); setMontoInput(""); setCambio(0);
    }
    setErrors({ tipoPago: false, abono: false, fechaCuota: false });
  };

  const handleMontoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    const num = Number(raw || 0);
    setMontoInput(formatMoney(num)); setAbono(num.toString());
    if (tipoPago === 1) setCambio(Math.max(0, num - totalAPagar));
    setErrors({ ...errors, abono: num <= 0 });
  };

  const handleProcesar = async () => {
    const errs = {
      tipoPago: !tipoPago,
      abono: !esCreditoTipo && (!parseFloat(abono) || parseFloat(abono) <= 0),
      fechaCuota: esCreditoTipo && !fechaPrimeraCuota,
    };
    setErrors(errs);
    if (Object.values(errs).some(Boolean) || isProcessing) return;
    setIsProcessing(true);
    try {
      await onPagar({
        IdVenta: idVenta,
        IdTipoPago: tipoPago,
        Abono: esCreditoTipo ? totalAPagar : parseFloat(abono),
        Observacion: observacion,
        CreditoInfo: esCreditoTipo
          ? { NumeroCuotas: numeroCuotas, FechaPrimeraCuota: fechaPrimeraCuota, IntervaloDias: intervaloDias }
          : undefined,
      });
      onPagoCompletado();
    } catch (e) {
      toast.error((e as Error).message);
      setIsProcessing(false);
    }
  };

  if (isLoading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress size={28} sx={{ color: p.accent }} /></Box>;

  return (
    <Box>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: p.accentBg, display: "flex", alignItems: "center", justifyContent: "center", color: p.accent, "& svg": { fontSize: 16 } }}>
            <PaymentIcon />
          </Box>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 650, color: p.text }}>Procesar Pago</Typography>
        </Stack>

        <Stack spacing={2.5}>
          {/* Total */}
          <Box sx={{ p: 2.5, borderRadius: "10px", border: `1px solid ${p.accent}`, bgcolor: p.accentBg }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
              <AccountBalanceWallet sx={{ fontSize: 14, color: p.textT }} />
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em" }}>Total a Pagar</Typography>
            </Stack>
            <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: p.accent, fontFamily: "'IBM Plex Mono', monospace" }}>{formatMoney(totalAPagar)}</Typography>
          </Box>

          {/* Tipo pago */}
          <Box>
            <FieldLabel label="Tipo de Pago" required color={p.textT} />
            <FormControl fullWidth size="small" error={errors.tipoPago}>
              <Select value={tipoPago} onChange={(e) => handleTipoPagoChange(Number(e.target.value))} displayEmpty
                sx={{ borderRadius: "8px", bgcolor: p.bg, fontSize: "0.88rem", "& .MuiOutlinedInput-notchedOutline": { borderColor: p.border }, "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: p.borderH }, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: p.accent } }}>
                <MenuItem value={0} disabled><Typography sx={{ color: p.textT, fontSize: "0.84rem" }}>Seleccione...</Typography></MenuItem>
                {tiposPago.map(t => <MenuItem key={t.Id} value={t.Id}><Stack direction="row" alignItems="center" spacing={1}><CreditCard sx={{ fontSize: 16, color: p.textT }} /><span>{t.Nombre}</span></Stack></MenuItem>)}
              </Select>
              {errors.tipoPago && <FormHelperText>Seleccione un tipo de pago</FormHelperText>}
            </FormControl>
          </Box>

          {/* Monto (solo para no-crédito) */}
          {tipoPago !== 0 && !esCreditoTipo && (
            <Fade in>
              <Box>
                <FieldLabel label="Monto a Pagar" required color={p.textT} />
                <TextField fullWidth size="small" value={montoInput} onChange={handleMontoChange} error={errors.abono} placeholder="$ 0"
                  helperText={errors.abono ? "Ingrese un monto válido" : ""}
                  InputProps={{ startAdornment: <InputAdornment position="start"><AttachMoney sx={{ fontSize: 18, color: p.accent }} /></InputAdornment> }}
                  sx={{ "& .MuiInputBase-root": { borderRadius: "8px", bgcolor: p.bg, fontSize: "1rem", fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: p.border } }}
                />
              </Box>
            </Fade>
          )}

          {/* Formulario de crédito */}
          {tipoPago !== 0 && esCreditoTipo && (
            <Fade in>
              <Box sx={{ p: 2.5, borderRadius: "10px", border: `1px solid ${p.accent}`, bgcolor: p.accentBg }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <CreditScore sx={{ fontSize: 16, color: p.accent }} />
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: p.accent, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Plan de Crédito
                  </Typography>
                </Stack>

                <Stack spacing={2}>
                  {/* Número de cuotas */}
                  <Box>
                    <FieldLabel label="Número de Cuotas" required color={p.textT} />
                    <FormControl fullWidth size="small">
                      <Select value={numeroCuotas} onChange={(e) => setNumeroCuotas(Number(e.target.value))}
                        sx={{ borderRadius: "8px", bgcolor: p.bg, fontSize: "0.88rem", "& .MuiOutlinedInput-notchedOutline": { borderColor: p.border } }}>
                        {[1, 2, 3, 6, 9, 12, 18, 24].map(n => (
                          <MenuItem key={n} value={n}>{n} cuota{n > 1 ? "s" : ""}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Intervalo */}
                  <Box>
                    <FieldLabel label="Frecuencia de pago" required color={p.textT} />
                    <FormControl fullWidth size="small">
                      <Select value={intervaloDias} onChange={(e) => setIntervaloDias(Number(e.target.value))}
                        sx={{ borderRadius: "8px", bgcolor: p.bg, fontSize: "0.88rem", "& .MuiOutlinedInput-notchedOutline": { borderColor: p.border } }}>
                        <MenuItem value={7}>Semanal (7 días)</MenuItem>
                        <MenuItem value={15}>Quincenal (15 días)</MenuItem>
                        <MenuItem value={30}>Mensual (30 días)</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Fecha primera cuota */}
                  <Box>
                    <FieldLabel label="Fecha primera cuota" required color={p.textT} />
                    <TextField fullWidth size="small" type="date" value={fechaPrimeraCuota}
                      onChange={(e) => setFechaPrimeraCuota(e.target.value)}
                      error={errors.fechaCuota}
                      helperText={errors.fechaCuota ? "Seleccione una fecha" : ""}
                      InputProps={{ startAdornment: <InputAdornment position="start"><CalendarMonth sx={{ fontSize: 16, color: p.textT }} /></InputAdornment> }}
                      sx={{ "& .MuiInputBase-root": { borderRadius: "8px", bgcolor: p.bg, fontSize: "0.88rem" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: p.border } }}
                    />
                  </Box>

                  {/* Resumen */}
                  <Divider sx={{ borderColor: p.border }} />
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ fontSize: "0.75rem", color: p.textT }}>Valor por cuota:</Typography>
                    <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: p.accent, fontFamily: "'IBM Plex Mono', monospace" }}>
                      {formatMoney(valorCuotaCalc)}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </Fade>
          )}

          {/* Cambio */}
          {tipoPago === 1 && cambio > 0 && (
            <Fade in>
              <Box sx={{ p: 2.5, borderRadius: "10px", border: `1px solid ${p.success}`, bgcolor: p.successBg }}>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                  <CheckCircle sx={{ fontSize: 14, color: p.success }} />
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: p.success, textTransform: "uppercase" }}>Cambio a devolver</Typography>
                </Stack>
                <Typography sx={{ fontSize: "1.4rem", fontWeight: 700, color: p.success, fontFamily: "'IBM Plex Mono', monospace" }}>{formatMoney(cambio)}</Typography>
              </Box>
            </Fade>
          )}

          {/* Observaciones */}
          <Box>
            <FieldLabel label="Observaciones" color={p.textT} />
            <TextField fullWidth size="small" multiline rows={2} value={observacion} onChange={(e) => setObservacion(e.target.value)} placeholder="Notas sobre este pago..."
              sx={{ "& .MuiInputBase-root": { borderRadius: "8px", bgcolor: p.bg, fontSize: "0.84rem" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: p.border } }}
            />
          </Box>

          {/* Pago parcial warning */}
          {tipoPago !== 0 && parseFloat(abono) > 0 && parseFloat(abono) < totalAPagar && (
            <Box sx={{ p: 2, borderRadius: "8px", bgcolor: p.warningBg, border: `1px solid ${alpha(p.warning, 0.2)}` }}>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: p.warning }}>Pago Parcial</Typography>
              <Typography sx={{ fontSize: "0.75rem", color: p.warning, mt: 0.3 }}>Saldo pendiente: {formatMoney(totalAPagar - parseFloat(abono))}</Typography>
            </Box>
          )}

          {/* Botón */}
          <Button variant="contained" fullWidth disableElevation
            startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : <PaidIcon sx={{ fontSize: 18 }} />}
            endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
            onClick={handleProcesar} disabled={!tipoPago || (!esCreditoTipo && !abono) || (esCreditoTipo && !fechaPrimeraCuota) || isProcessing}
            sx={{
              bgcolor: p.success, color: "#FFF", py: 1.3, fontSize: "0.88rem", fontWeight: 700,
              borderRadius: "8px", "&:hover": { bgcolor: darkMode ? "#5DC0AD" : "#0A6B5E" },
              "&.Mui-disabled": { bgcolor: alpha(p.success, 0.15), color: alpha(p.text, 0.3) },
            }}
          >
            {isProcessing ? "Procesando..." : esCreditoTipo ? "Registrar Crédito" : "Completar Pago"}
          </Button>
        </Stack>
      </Box>
  );
};
